import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  ClockIcon, 
  KeyIcon, 
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const SecuritySettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 1440,
    passwordMinLength: 8,
    requireTwoFactor: false,
    loginAttempts: 5
  });

  useEffect(() => {
    if (settings?.security) {
      setSecuritySettings(settings.security);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newSecuritySettings = {
      ...securitySettings,
      [field]: value
    };
    setSecuritySettings(newSecuritySettings);
    onSettingsChange({ ...settings, security: newSecuritySettings });
    onHasChanges(true);
  };

  const formatSessionTimeout = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} heures`;
    return `${Math.floor(minutes / 1440)} jours`;
  };

  const getSecurityLevel = () => {
    let score = 0;
    if (securitySettings.sessionTimeout <= 480) score += 1;
    if (securitySettings.passwordMinLength >= 12) score += 1;
    if (securitySettings.requireTwoFactor) score += 2;
    if (securitySettings.loginAttempts <= 3) score += 1;
    
    if (score >= 4) return { level: 'Élevé', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 2) return { level: 'Moyen', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    return { level: 'Faible', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const securityLevel = getSecurityLevel();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheckIcon className="h-6 w-6 text-red-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Paramètres de sécurité</h3>
          <p className="text-white/60 text-sm">Configuration de la sécurité et de l'authentification</p>
        </div>
      </div>

      {/* Niveau de sécurité */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`${securityLevel.bg} backdrop-blur-lg rounded-2xl p-6 border border-white/20`}
      >
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheckIcon className={`h-6 w-6 ${securityLevel.color}`} />
          <div>
            <h4 className="font-semibold text-white">Niveau de sécurité</h4>
            <p className={`text-sm ${securityLevel.color}`}>{securityLevel.level}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            {securitySettings.sessionTimeout <= 480 ? (
              <CheckCircleIcon className="h-4 w-4 text-green-400" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-red-400" />
            )}
            <span className="text-white/80">Session courte</span>
          </div>
          <div className="flex items-center gap-2">
            {securitySettings.passwordMinLength >= 12 ? (
              <CheckCircleIcon className="h-4 w-4 text-green-400" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-red-400" />
            )}
            <span className="text-white/80">Mot de passe fort</span>
          </div>
          <div className="flex items-center gap-2">
            {securitySettings.requireTwoFactor ? (
              <CheckCircleIcon className="h-4 w-4 text-green-400" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-red-400" />
            )}
            <span className="text-white/80">2FA activé</span>
          </div>
          <div className="flex items-center gap-2">
            {securitySettings.loginAttempts <= 3 ? (
              <CheckCircleIcon className="h-4 w-4 text-green-400" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-red-400" />
            )}
            <span className="text-white/80">Tentatives limitées</span>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Timeout de session */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <ClockIcon className="h-5 w-5 text-blue-400" />
              <h4 className="font-semibold text-white">Timeout de session</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value) || 1440)}
                  min="5"
                  max="10080"
                  className="w-24 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">minutes</span>
              </div>
              
              <div className="text-sm text-white/60">
                Équivalent à : {formatSessionTimeout(securitySettings.sessionTimeout)}
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[30, 60, 480, 1440].map(minutes => (
                  <button
                    key={minutes}
                    onClick={() => handleChange('sessionTimeout', minutes)}
                    className={`p-2 rounded-lg transition-colors ${
                      securitySettings.sessionTimeout === minutes 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {formatSessionTimeout(minutes)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Longueur minimale du mot de passe */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <KeyIcon className="h-5 w-5 text-green-400" />
              <h4 className="font-semibold text-white">Mot de passe minimum</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={securitySettings.passwordMinLength}
                  onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value) || 8)}
                  min="6"
                  max="20"
                  className="w-20 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">caractères</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Niveau de sécurité :</p>
                <div className="flex items-center gap-2">
                  {securitySettings.passwordMinLength >= 12 ? (
                    <>
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 text-sm">Fort</span>
                    </>
                  ) : securitySettings.passwordMinLength >= 8 ? (
                    <>
                      <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400" />
                      <span className="text-yellow-400 text-sm">Moyen</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-4 w-4 text-red-400" />
                      <span className="text-red-400 text-sm">Faible</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-sm">
                {[8, 12, 16].map(length => (
                  <button
                    key={length}
                    onClick={() => handleChange('passwordMinLength', length)}
                    className={`p-2 rounded-lg transition-colors ${
                      securitySettings.passwordMinLength === length 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {length} car.
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Authentification à deux facteurs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <DevicePhoneMobileIcon className="h-5 w-5 text-purple-400" />
              <h4 className="font-semibold text-white">Authentification à deux facteurs</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Activer l'authentification à deux facteurs</p>
                  <p className="text-white/60 text-xs">Sécurité renforcée avec code SMS/Email</p>
                </div>
                <button
                  onClick={() => handleChange('requireTwoFactor', !securitySettings.requireTwoFactor)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    securitySettings.requireTwoFactor ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      securitySettings.requireTwoFactor ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className={`bg-white/5 rounded-xl p-3 ${
                securitySettings.requireTwoFactor ? 'border border-green-500/20' : 'border border-red-500/20'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {securitySettings.requireTwoFactor ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-400" />
                  )}
                  <p className="text-white/80 text-sm font-medium">
                    {securitySettings.requireTwoFactor ? 'Activé' : 'Désactivé'}
                  </p>
                </div>
                <p className="text-white/60 text-xs">
                  {securitySettings.requireTwoFactor 
                    ? 'Un code de vérification sera demandé lors de la connexion'
                    : 'Connexion avec mot de passe uniquement'
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tentatives de connexion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <LockClosedIcon className="h-5 w-5 text-red-400" />
              <h4 className="font-semibold text-white">Tentatives de connexion</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={securitySettings.loginAttempts}
                  onChange={(e) => handleChange('loginAttempts', parseInt(e.target.value) || 5)}
                  min="1"
                  max="10"
                  className="w-20 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">tentatives</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Sécurité :</p>
                <div className="flex items-center gap-2">
                  {securitySettings.loginAttempts <= 3 ? (
                    <>
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 text-sm">Élevée</span>
                    </>
                  ) : securitySettings.loginAttempts <= 5 ? (
                    <>
                      <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400" />
                      <span className="text-yellow-400 text-sm">Moyenne</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-4 w-4 text-red-400" />
                      <span className="text-red-400 text-sm">Faible</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-sm">
                {[3, 5, 10].map(attempts => (
                  <button
                    key={attempts}
                    onClick={() => handleChange('loginAttempts', attempts)}
                    className={`p-2 rounded-lg transition-colors ${
                      securitySettings.loginAttempts === attempts 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {attempts} tent.
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Résumé des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Résumé des paramètres de sécurité</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <ClockIcon className="h-6 w-6 text-blue-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-white">
              {formatSessionTimeout(securitySettings.sessionTimeout)}
            </div>
            <div className="text-white/60 text-xs">Timeout session</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <KeyIcon className="h-6 w-6 text-green-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{securitySettings.passwordMinLength}</div>
            <div className="text-white/60 text-xs">Caractères min</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <DevicePhoneMobileIcon className="h-6 w-6 text-purple-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-white">
              {securitySettings.requireTwoFactor ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-white/60 text-xs">2FA</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <LockClosedIcon className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{securitySettings.loginAttempts}</div>
            <div className="text-white/60 text-xs">Tentatives max</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SecuritySettings; 